import * as THREE from "three";

/**
 * The minotaur — guardian of the labyrinth. A fully procedural rig (no glTF,
 * no loaders) with four hand-authored, procedurally-blended clips: idle, walk,
 * attack, roar. Ported from the standalone model viewer into a reusable module:
 * `buildMinotaur(scale)` returns a `root` group (feet grounded at root.y = 0),
 * a `setAnim(name)` switch and an `update(dt)` to advance the animation.
 *
 * The clips are written into a target pose each frame, then every joint eases
 * toward it (frame-rate-independent), so switching clips blends smoothly.
 */

export type MinotaurAnim = "idle" | "walk" | "attack" | "roar";

export interface Minotaur {
  /** Position / rotate this in the world; feet rest at root.position.y = 0. */
  root: THREE.Group;
  setAnim(name: MinotaurAnim): void;
  current(): MinotaurAnim;
  update(dt: number): void;
}

/* ---------- shared materials ---------- */
function materials() {
  return {
    fur: new THREE.MeshStandardMaterial({ color: 0x5a3a22, roughness: 0.82 }),
    furDark: new THREE.MeshStandardMaterial({ color: 0x3c2616, roughness: 0.9 }),
    muzzle: new THREE.MeshStandardMaterial({ color: 0x6b4a30, roughness: 0.78 }),
    horn: new THREE.MeshStandardMaterial({ color: 0xe9e2cf, roughness: 0.4 }),
    hornTip: new THREE.MeshStandardMaterial({ color: 0x4a4034, roughness: 0.5 }),
    hoof: new THREE.MeshStandardMaterial({ color: 0x1c1611, roughness: 0.35 }),
    black: new THREE.MeshStandardMaterial({ color: 0x0a0807, roughness: 0.6 }),
    eye: new THREE.MeshStandardMaterial({ color: 0xff8a1e, emissive: 0xff5a00, emissiveIntensity: 2.0, roughness: 0.3 }),
    brass: new THREE.MeshStandardMaterial({ color: 0xc9a24a, roughness: 0.35, metalness: 1.0 }),
    cloth: new THREE.MeshStandardMaterial({ color: 0x6e2a1e, roughness: 0.95, side: THREE.DoubleSide }),
    leather: new THREE.MeshStandardMaterial({ color: 0x402013, roughness: 0.9 }),
  };
}

const UNIT_SPHERE = new THREE.SphereGeometry(1, 24, 16);

export function buildMinotaur(scale = 0.5): Minotaur {
  const M = materials();

  /* ---------- helpers ---------- */
  const ellipsoid = (parent: THREE.Object3D, r: number[], pos: number[], mat: THREE.Material, rot?: number[]) => {
    const m = new THREE.Mesh(UNIT_SPHERE, mat);
    m.scale.set(r[0], r[1], r[2]); m.position.set(pos[0], pos[1], pos[2]);
    if (rot) m.rotation.set(rot[0], rot[1], rot[2]);
    m.castShadow = true; parent.add(m); return m;
  };
  const capsule = (parent: THREE.Object3D, radius: number, length: number, pos: number[], rot: number[] | null, mat: THREE.Material) => {
    const m = new THREE.Mesh(new THREE.CapsuleGeometry(radius, length, 6, 12), mat);
    m.position.set(pos[0], pos[1], pos[2]);
    if (rot) m.rotation.set(rot[0], rot[1], rot[2]);
    m.castShadow = true; parent.add(m); return m;
  };
  const mesh = (parent: THREE.Object3D, geo: THREE.BufferGeometry, mat: THREE.Material, pos?: number[], rot?: number[]) => {
    const m = new THREE.Mesh(geo, mat);
    if (pos) m.position.set(pos[0], pos[1], pos[2]);
    if (rot) m.rotation.set(rot[0], rot[1], rot[2]);
    m.castShadow = true; parent.add(m); return m;
  };
  const pivot = (parent: THREE.Object3D, x: number, y: number, z: number) => {
    const g = new THREE.Group(); g.position.set(x, y, z); parent.add(g); return g;
  };
  const taperedTube = (parent: THREE.Object3D, pts: THREE.Vector3[], radii: number[], mat: THREE.Material, tipMat?: THREE.Material) => {
    const up = new THREE.Vector3(0, 1, 0);
    const tipStart = pts.length - 3;
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i], b = pts[i + 1];
      const dir = new THREE.Vector3().subVectors(b, a);
      const len = dir.length();
      const segMat = (tipMat && i >= tipStart) ? tipMat : mat;
      const seg = new THREE.Mesh(new THREE.CylinderGeometry(radii[i + 1], radii[i], len, 12), segMat);
      seg.position.copy(a).addScaledVector(dir, 0.5);
      seg.quaternion.setFromUnitVectors(up, dir.clone().normalize());
      seg.castShadow = true; parent.add(seg);
      const js = new THREE.Mesh(new THREE.SphereGeometry(radii[i], 10, 8), segMat);
      js.position.copy(a); js.castShadow = true; parent.add(js);
    }
  };

  /* ---------- rig ---------- */
  const rig = new THREE.Group();

  const spine = pivot(rig, 0, 2.25, 0);
  ellipsoid(spine, [0.55, 0.50, 0.45], [0, 0.25, 0.02], M.fur);
  ellipsoid(spine, [0.85, 0.62, 0.55], [0, 0.75, 0.00], M.fur);
  ellipsoid(spine, [0.70, 0.50, 0.50], [0, 0.95, -0.18], M.furDark);
  ellipsoid(spine, [0.34, 0.30, 0.24], [-0.34, 0.73, 0.40], M.fur);
  ellipsoid(spine, [0.34, 0.30, 0.24], [0.34, 0.73, 0.40], M.fur);
  for (const s of [-1, 1]) {
    ellipsoid(spine, [0.15, 0.15, 0.12], [s * 0.16, 0.37, 0.40], M.fur);
    ellipsoid(spine, [0.14, 0.14, 0.11], [s * 0.15, 0.15, 0.38], M.fur);
  }
  capsule(spine, 0.30, 0.18, [0, 1.05, 0.05], [0.15, 0, 0], M.furDark);
  ellipsoid(spine, [0.32, 0.32, 0.32], [-0.80, 0.80, 0.03], M.fur);
  ellipsoid(spine, [0.32, 0.32, 0.32], [0.80, 0.80, 0.03], M.fur);
  ellipsoid(spine, [0.62, 0.50, 0.50], [0, -0.15, 0.00], M.fur);
  mesh(spine, new THREE.CylinderGeometry(0.50, 0.82, 0.75, 20, 1, true), M.cloth, [0, -0.47, 0.0]);
  mesh(spine, new THREE.BoxGeometry(0.34, 0.55, 0.06), M.cloth, [0, -0.53, 0.60], [0.12, 0, 0]);
  mesh(spine, new THREE.TorusGeometry(0.56, 0.07, 10, 24), M.leather, [0, -0.07, 0.0], [Math.PI / 2, 0, 0]);
  mesh(spine, new THREE.BoxGeometry(0.16, 0.14, 0.06), M.brass, [0, -0.07, 0.57]);

  const shoulders: Record<string, THREE.Group> = {}, elbows: Record<string, THREE.Group> = {};
  for (const [side, s] of [["L", -1], ["R", 1]] as const) {
    const sh = pivot(spine, s * 0.80, 0.80, 0.03);
    shoulders[side] = sh;
    capsule(sh, 0.25, 0.50, [s * 0.12, -0.43, 0.01], [0, 0, s * 0.16], M.fur);
    ellipsoid(sh, [0.22, 0.26, 0.22], [s * 0.15, -0.43, 0.09], M.fur);
    const el = pivot(sh, s * 0.22, -0.85, 0.03);
    elbows[side] = el;
    ellipsoid(el, [0.22, 0.22, 0.22], [0, 0, 0], M.fur);
    capsule(el, 0.22, 0.46, [s * 0.04, -0.34, 0.12], [-0.25, 0, s * 0.10], M.fur);
    ellipsoid(el, [0.17, 0.17, 0.17], [s * 0.06, -0.65, 0.28], M.fur);
    ellipsoid(el, [0.25, 0.22, 0.26], [s * 0.06, -0.78, 0.36], M.furDark);
    for (let j = -1; j <= 1; j++)
      ellipsoid(el, [0.06, 0.07, 0.06], [s * 0.06 + j * 0.07, -0.70, 0.48], M.furDark);
    capsule(el, 0.06, 0.10, [s * -0.09, -0.74, 0.40], [0.4, 0, s * 0.6], M.furDark);
  }

  const hips: Record<string, THREE.Group> = {}, knees: Record<string, THREE.Group> = {};
  for (const [side, s] of [["L", -1], ["R", 1]] as const) {
    const hip = pivot(rig, s * 0.50, 1.98, 0.04);
    hips[side] = hip;
    capsule(hip, 0.34, 0.62, [0, -0.30, 0.0], [0, 0, s * 0.03], M.fur);
    const knee = pivot(hip, 0, -0.94, 0.08);
    knees[side] = knee;
    ellipsoid(knee, [0.30, 0.30, 0.30], [0, 0, 0], M.fur);
    capsule(knee, 0.25, 0.55, [0, -0.26, -0.06], [-0.06, 0, 0], M.fur);
    ellipsoid(knee, [0.24, 0.22, 0.24], [0, -0.70, -0.08], M.furDark);
    mesh(knee, new THREE.CylinderGeometry(0.22, 0.28, 0.30, 16), M.hoof, [0, -0.89, -0.02]);
    mesh(knee, new THREE.BoxGeometry(0.03, 0.18, 0.16), M.black, [0, -0.92, 0.12]);
  }

  const head = pivot(spine, 0, 1.17, 0.05);
  ellipsoid(head, [0.43, 0.46, 0.50], [0, 0.40, 0.00], M.fur);
  ellipsoid(head, [0.34, 0.30, 0.34], [0, 0.18, 0.18], M.fur);
  mesh(head, new THREE.BoxGeometry(0.50, 0.14, 0.20), M.furDark, [0, 0.52, 0.30], [-0.15, 0, 0]);
  mesh(head, new THREE.CylinderGeometry(0.24, 0.36, 0.50, 16), M.muzzle, [0, 0.20, 0.52], [Math.PI / 2, 0, 0]);
  ellipsoid(head, [0.28, 0.20, 0.34], [0, 0.06, 0.45], M.muzzle);
  ellipsoid(head, [0.20, 0.15, 0.12], [0, 0.18, 0.78], M.black);
  ellipsoid(head, [0.05, 0.06, 0.04], [-0.09, 0.15, 0.82], M.black);
  ellipsoid(head, [0.05, 0.06, 0.04], [0.09, 0.15, 0.82], M.black);
  mesh(head, new THREE.BoxGeometry(0.26, 0.025, 0.18), M.black, [0, 0.04, 0.66]);
  for (const s of [-1, 1]) {
    ellipsoid(head, [0.12, 0.12, 0.08], [s * 0.26, 0.46, 0.34], M.furDark);
    mesh(head, new THREE.SphereGeometry(0.08, 14, 12), M.eye, [s * 0.27, 0.46, 0.40]);
    mesh(head, new THREE.SphereGeometry(0.035, 10, 8), M.black, [s * 0.28, 0.46, 0.47]);
    // eyes glow as beacons so the beast reads even deep in the dark hall
    const glow = new THREE.PointLight(0xff5a12, 1.4, 5.5, 2.0);
    glow.position.set(s * 0.27, 0.46, 0.55); head.add(glow);
  }
  for (const s of [-1, 1]) {
    ellipsoid(head, [0.09, 0.20, 0.15], [s * 0.46, 0.42, -0.02], M.fur, [0, -s * 0.5, s * 0.7]);
    ellipsoid(head, [0.05, 0.15, 0.10], [s * 0.50, 0.42, 0.02], M.muzzle, [0, -s * 0.5, s * 0.7]);
  }
  for (let i = -1; i <= 1; i++)
    mesh(head, new THREE.ConeGeometry(0.06, 0.22, 8), M.furDark, [i * 0.12, 0.62, 0.18], [-0.3, 0, i * 0.2]);
  for (let i = -1; i <= 1; i++)
    mesh(head, new THREE.ConeGeometry(0.05, 0.18, 8), M.furDark, [i * 0.08, -0.02, 0.38], [Math.PI - 0.2, 0, 0]);
  const hornPtsR = [
    new THREE.Vector3(0.27, 0.63, 0.07), new THREE.Vector3(0.50, 0.73, 0.15),
    new THREE.Vector3(0.68, 0.93, 0.23), new THREE.Vector3(0.78, 1.20, 0.33),
    new THREE.Vector3(0.80, 1.43, 0.45), new THREE.Vector3(0.78, 1.58, 0.57),
  ];
  const hornR = [0.13, 0.11, 0.085, 0.06, 0.03, 0.012];
  taperedTube(head, hornPtsR, hornR, M.horn, M.hornTip);
  taperedTube(head, hornPtsR.map((p) => new THREE.Vector3(-p.x, p.y, p.z)), hornR, M.horn, M.hornTip);
  ellipsoid(head, [0.15, 0.12, 0.15], [0.27, 0.60, 0.07], M.fur);
  ellipsoid(head, [0.15, 0.12, 0.15], [-0.27, 0.60, 0.07], M.fur);
  mesh(head, new THREE.TorusGeometry(0.11, 0.025, 10, 20), M.brass, [0, 0.03, 0.80]);

  const tail = pivot(rig, 0, 2.00, -0.45);
  const tailPts = [
    new THREE.Vector3(0.00, 0.00, 0.00), new THREE.Vector3(0.00, -0.10, -0.15),
    new THREE.Vector3(0.05, -0.40, -0.25), new THREE.Vector3(0.05, -0.80, -0.20),
    new THREE.Vector3(0.00, -1.10, -0.10), new THREE.Vector3(-0.05, -1.30, 0.00),
  ];
  const tailR = [0.10, 0.09, 0.07, 0.05, 0.035, 0.02];
  taperedTube(tail, tailPts, tailR, M.fur);
  ellipsoid(tail, [0.10, 0.12, 0.10], [-0.05, -1.36, 0.0], M.furDark);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    mesh(tail, new THREE.ConeGeometry(0.04, 0.16, 7), M.furDark,
      [-0.05 + Math.cos(a) * 0.04, -1.44, Math.sin(a) * 0.04], [Math.PI, 0, 0]);
  }

  /* The original model faces +z (muzzle, eyes, horns curl forward). Keep that:
     the Stage rotates `root` so the beast faces its march direction. */
  const root = new THREE.Group();
  const BASE_Y = -0.12 * scale; // drop so the hooves rest on the floor
  rig.scale.setScalar(scale);
  rig.position.y = BASE_Y;
  root.add(rig);

  /* ---------- animation ---------- */
  type Joint = THREE.Group;
  const RIG: Record<string, Joint> = {
    spine, hipL: hips.L, hipR: hips.R, kneeL: knees.L, kneeR: knees.R,
    shL: shoulders.L, shR: shoulders.R, elL: elbows.L, elR: elbows.R, head, tail,
  };
  const JOINTS = Object.keys(RIG);
  type Vec = { x: number; y: number; z: number };
  const T: Record<string, Vec> = {};
  JOINTS.forEach((j) => (T[j] = { x: 0, y: 0, z: 0 }));
  let rootY = 0;

  const zeroTargets = (): void => {
    for (const j of JOINTS) { const o = T[j]; o.x = 0; o.y = 0; o.z = 0; }
    rootY = 0;
  };
  const seg = (x: number, a: number, b: number) => Math.min(1, Math.max(0, (x - a) / (b - a)));
  const sstep = (x: number) => x * x * (3 - 2 * x);

  const animIdle = (t: number): void => {
    rootY = Math.sin(t * 1.2) * 0.012;
    T.spine.x = Math.sin(t * 1.2) * 0.015;
    T.spine.z = Math.sin(t * 0.55) * 0.02;
    T.head.y = Math.sin(t * 0.5) * 0.08;
    T.head.x = Math.sin(t * 0.8) * 0.03 - 0.02;
    T.shL.x = Math.sin(t * 0.7) * 0.03;
    T.shR.x = Math.sin(t * 0.7 + 0.5) * 0.03;
    T.elL.x = -0.15 + Math.sin(t * 0.7) * 0.02;
    T.elR.x = -0.15 + Math.sin(t * 0.7 + 0.5) * 0.02;
    T.tail.z = Math.sin(t * 1.1) * 0.16;
    T.tail.x = 0.05 + Math.sin(t * 0.7) * 0.08;
  };
  const animWalk = (t: number): void => {
    const p = t * 4.2, sp = Math.sin(p);
    const lL = Math.max(0, sp), lR = Math.max(0, -sp);
    T.hipL.x = -lL * 0.6; T.hipR.x = -lR * 0.6;
    T.kneeL.x = lL * 0.95 + 0.05; T.kneeR.x = lR * 0.95 + 0.05;
    rootY = Math.abs(sp) * 0.05;
    T.shL.x = sp * 0.30; T.shR.x = -sp * 0.30;
    T.elL.x = -0.45; T.elR.x = -0.45;
    T.spine.y = sp * 0.08; T.spine.z = sp * 0.03; T.spine.x = 0.04;
    T.head.x = Math.abs(sp) * 0.03 - 0.02; T.head.y = -sp * 0.05;
    T.tail.z = sp * 0.22; T.tail.x = 0.12 + Math.abs(sp) * 0.06;
  };
  const animAttack = (t: number): void => {
    const a = (t % 2.3) / 2.3;
    const wind = sstep(seg(a, 0, 0.32)) * (1 - sstep(seg(a, 0.32, 0.46)));
    const slam = sstep(seg(a, 0.32, 0.46)) * (1 - sstep(seg(a, 0.70, 1.0)));
    const shX = -2.4 * wind - 0.5 * slam;
    T.shL.x = shX; T.shR.x = shX;
    T.shL.z = -0.10 * wind; T.shR.z = 0.10 * wind;
    T.elL.x = -0.7 * wind - 0.1 * slam; T.elR.x = T.elL.x;
    T.spine.x = -0.18 * wind + 0.38 * slam;
    T.head.x = -0.30 * wind + 0.45 * slam;
    rootY = 0.06 * wind - 0.10 * slam;
    T.kneeL.x = 0.05 + 0.30 * slam; T.kneeR.x = T.kneeL.x;
    T.hipL.x = 0.10 * slam; T.hipR.x = 0.10 * slam;
    T.tail.x = 0.10 + 0.5 * wind;
  };
  const animRoar = (t: number): void => {
    const a = (t % 3.0) / 3.0;
    const up = sstep(seg(a, 0, 0.25)) * (1 - sstep(seg(a, 0.70, 1.0)));
    const hold = (a > 0.25 && a < 0.72) ? 1 : 0;
    const heave = hold ? Math.sin(t * 7) * 0.03 : 0;
    T.head.x = -0.42 * up + heave;
    T.spine.x = -0.16 * up + heave * 0.4;
    T.shL.x = -0.30 * up; T.shR.x = -0.30 * up;
    T.shL.z = -0.70 * up; T.shR.z = 0.70 * up;
    T.elL.x = -0.90 * up; T.elR.x = -0.90 * up;
    rootY = 0.05 * up;
    T.hipL.x = 0.06 * up; T.hipR.x = 0.06 * up;
    T.kneeL.x = 0.10 * up; T.kneeR.x = 0.10 * up;
    T.tail.x = 0.10 + 0.60 * up;
    T.tail.z = hold ? Math.sin(t * 6) * 0.20 : 0;
  };

  const anims: Record<MinotaurAnim, (t: number) => void> = {
    idle: animIdle, walk: animWalk, attack: animAttack, roar: animRoar,
  };
  let currentAnim: MinotaurAnim = "idle";
  let animTime = 0;
  const RATE = 16;

  return {
    root,
    setAnim(name: MinotaurAnim) { if (name !== currentAnim) { currentAnim = name; animTime = 0; } },
    current() { return currentAnim; },
    update(dt: number) {
      const d = Math.min(dt, 0.05);
      animTime += d;
      zeroTargets();
      anims[currentAnim](animTime);
      const f = 1 - Math.exp(-RATE * d);
      for (const j of JOINTS) {
        const o = T[j], r = RIG[j].rotation;
        r.x += (o.x - r.x) * f;
        r.y += (o.y - r.y) * f;
        r.z += (o.z - r.z) * f;
      }
      const targetY = BASE_Y + rootY * scale;
      rig.position.y += (targetY - rig.position.y) * f;
    },
  };
}
